import React, { useState } from 'react';
import Modal from 'react-modal';
import { userAPI } from '../services/api';
import { toast } from 'react-toastify';

// Set app element safely
if (typeof document !== 'undefined') {
  const rootElement = document.getElementById('root');
  if (rootElement) {
    Modal.setAppElement('#root');
  }
}

const PhotoUploadModal = ({
  isOpen,
  onClose,
  onUploadSuccess,
  allowProfilePhoto = false,
  defaultTab = 'gallery',
  currentNormalPhotos = 0,
  currentPrivatePhotos = 0,
  currentProfilePhotos = 0,
  maxNormalPhotos = 6,
  maxPrivatePhotos = 6,
  maxProfilePhotos = 1
}) => {
  const [activeTab, setActiveTab] = useState(defaultTab); // 'gallery', 'private', or 'profile'
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [selectionMode, setSelectionMode] = useState('single'); // 'single' or 'multiple'

  console.log('🎭 PhotoUploadModal render:', { isOpen, activeTab, currentNormalPhotos, currentPrivatePhotos, currentProfilePhotos });

  const handleFileSelect = (e) => {
    console.log('🖼️ handleFileSelect called, files:', e.target.files);
    const files = Array.from(e.target.files);
    console.log('📁 Files array:', files);

    // Photo type'a göre dosya limiti hesapla (önce limit kontrolü)
    const maxFiles = activeTab === 'profile' ? Math.max(0, maxProfilePhotos - currentProfilePhotos) :
                     activeTab === 'gallery' ? Math.max(0, maxNormalPhotos - currentNormalPhotos) :
                     Math.max(0, maxPrivatePhotos - currentPrivatePhotos);

    console.log('📊 Max files calculated:', maxFiles, 'for tab:', activeTab);

    // Check if limit reached
    if (maxFiles === 0) {
      toast.error(`You have reached the maximum limit of ${activeTab === 'profile' ? '1 profile' : activeTab === 'gallery' ? '6 public' : '6 private'} photos. Please delete some photos first.`);
      return;
    }

    // Limit files to remaining capacity
    const filesToProcess = files.slice(0, maxFiles);
    if (files.length > maxFiles) {
      toast.warning(`You can only upload ${maxFiles} more ${activeTab === 'profile' ? 'profile' : activeTab === 'gallery' ? 'public' : 'private'} photo${maxFiles !== 1 ? 's' : ''}. Only first ${maxFiles} file${maxFiles !== 1 ? 's' : ''} selected.`);
    }

    const validFiles = [];

    // Çoklu dosya için validasyon
    for (const file of filesToProcess) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name}: Please select only image files`);
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name}: File size must be less than 5MB`);
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) {
      return;
    }

    // Dosya sayısı kontrolü (maxFiles zaten hesaplandı)
    if (validFiles.length > maxFiles) {
      toast.error(`You can select maximum ${maxFiles} photo${maxFiles === 1 ? '' : 's'} at once`);
      return;
    }

    // Tüm valid dosyalar için preview oluştur
    const previewPromises = validFiles.map((file, index) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve({
            file,
            preview: reader.result,
            id: `file-${index}-${Date.now()}`,
            type: activeTab // 'gallery' or 'private'
          });
        };
        reader.readAsDataURL(file);
      });
    });

    Promise.all(previewPromises).then((results) => {
      if (selectionMode === 'single') {
        // Tekli seçimde önceki seçimleri temizle
        setSelectedFiles(results.map(r => r.file));
        setPreviews(results);
      } else {
        // Çoklu seçimde ekleme yap
        setSelectedFiles(prev => [...prev, ...results.map(r => r.file)]);
        setPreviews(prev => [...prev, ...results]);
      }
    });
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (selectedFiles.length === 0) {
      toast.error('Please select at least one photo');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress({});

      const uploadPromises = previews.map(async (preview, index) => {
        const formData = new FormData();
        formData.append('photo', preview.file);
        formData.append('photo_type', preview.type === 'gallery' ? 'normal' : preview.type === 'profile' ? 'profile' : 'private');

        try {
          const response = await userAPI.uploadPhoto(formData);
          setUploadProgress(prev => ({
            ...prev,
            [index]: { status: 'success', message: '✓ Uploaded' }
          }));
          return response;
        } catch (error) {
          console.error(`Upload error for ${preview.file.name}:`, error);
          setUploadProgress(prev => ({
            ...prev,
            [index]: { status: 'error', message: `✗ ${error.message || 'Failed'}` }
          }));
          // Don't throw error, continue with other uploads
          return null;
        }
      });

      const results = await Promise.allSettled(uploadPromises);

      const successCount = results.filter(r => r.status === 'fulfilled').length;
      const errorCount = results.filter(r => r.status === 'rejected').length;

      if (successCount > 0) {
        toast.success(`${successCount} photo(s) uploaded successfully!`);
        if (errorCount > 0) {
          toast.warning(`${errorCount} photo(s) failed to upload`);
        }

      } else {
        toast.error('All uploads failed');
      }

      // Reset form
      setSelectedFiles([]);
      setPreviews([]);

      // Call success callback (don't refresh page)
      onUploadSuccess(successCount > 0);
      onClose();
    } catch (error) {
      console.error('Photo upload error:', error);
      toast.error('Upload process failed');
    } finally {
      setUploading(false);
      setUploadProgress({});
    }
  };

  console.log('🎭 Rendering PhotoUploadModal, isOpen:', isOpen);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="ReactModal__Content photo-upload-modal-content"
      overlayClassName="ReactModal__Overlay photo-upload-modal-overlay"
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
      style={{
        content: {
          position: 'relative',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
          animation: 'modalSlideIn 0.3s ease-out',
        },
        overlay: {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: 'modalFadeIn 0.3s ease-out',
        }
      }}
    >
      <div className="photo-upload-modal-container">
        {/* Fixed Header */}
        <div className="photo-upload-modal-header">
          <div className="modal-header">
            <h4 className="modal-title">
              Add Photos
              {selectedFiles.length > 0 && (
                <span className="selected-count"> ({selectedFiles.length})</span>
              )}
            </h4>
            <button type="button" className="close-button" onClick={onClose}>
              &times;
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="photo-upload-tabs">
            {allowProfilePhoto && (
              <button
                type="button"
                className={`photo-upload-tab ${activeTab === 'profile' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('profile');
                  // Profile photos are always single
                  setSelectionMode('single');
                  // Clear selections when switching tabs
                  setSelectedFiles([]);
                  setPreviews([]);
                }}
              >
                👤 Profile Photo
              </button>
            )}
            <button
              type="button"
              className={`photo-upload-tab ${activeTab === 'gallery' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('gallery');
                // Clear selections when switching tabs
                setSelectedFiles([]);
                setPreviews([]);
              }}
            >
              📸 Gallery Photos
            </button>
            <button
              type="button"
              className={`photo-upload-tab ${activeTab === 'private' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('private');
                // Clear selections when switching tabs
                setSelectedFiles([]);
                setPreviews([]);
              }}
            >
              🔒 Private Photos
            </button>
          </div>

          {/* Selection Mode Toggle - Profile için gizle */}
          {activeTab !== 'profile' && (
            <div className="selection-mode-toggle">
              <span className="selection-mode-label">Selection Mode:</span>
              <div className="selection-mode-buttons">
                <button
                  type="button"
                  className={`selection-mode-btn ${selectionMode === 'single' ? 'active' : ''}`}
                  onClick={() => {
                    setSelectionMode('single');
                    // Clear selections when switching to single mode
                    setSelectedFiles([]);
                    setPreviews([]);
                  }}
                >
                  Single
                </button>
                <button
                  type="button"
                  className={`selection-mode-btn ${selectionMode === 'multiple' ? 'active' : ''}`}
                  onClick={() => {
                    setSelectionMode('multiple');
                    // Clear selections when switching to multiple mode
                    setSelectedFiles([]);
                    setPreviews([]);
                  }}
                >
                  Multiple
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Scrollable Content */}
        <div className="photo-upload-modal-body">
          <div className="modal-body">
            <form onSubmit={handleUpload}>
              <input
              type="file"
              id="photo_file"
              accept="image/*"
              multiple={selectionMode === 'multiple' && activeTab !== 'profile'}
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />

            <div className="photo-upload-section">
              <div className="photo-upload-info">
                <h5>
                  {activeTab === 'profile' ? '👤 Profile Photo' :
                   activeTab === 'gallery' ? '📸 Gallery Photos' : '🔒 Private Photos'}
                </h5>
                <p>
                  {activeTab === 'profile'
                    ? 'This will be your main profile picture visible to everyone'
                    : activeTab === 'gallery'
                    ? 'These photos will be visible to everyone on your profile'
                    : 'These photos will only be visible to people you accept'
                  }
                </p>
                <p className="photo-limits">
                  {activeTab === 'profile'
                    ? 'Select 1 profile photo (max 5MB)'
                    : 'Select up to 6 photos (max 5MB each)'
                  }
                </p>
              </div>

              <button
                type="button"
                className="btn-primary photo-select-button"
                onClick={() => document.getElementById('photo_file').click()}
              >
                {activeTab === 'profile' ? 'Select Profile Photo' : 'Select Photos'}
              </button>
            </div>

            <div className="preview-area">
              {previews.length > 0 ? (
                <div className="photo-previews-grid">
                  {previews.map((item, index) => (
                    <div key={item.id} className="photo-preview-item">
                      <img
                        src={item.preview}
                        className="photo-upload-preview"
                        alt={`Preview ${index + 1}`}
                      />
                      <div className="photo-preview-info">
                        <span className="file-name">{item.file.name}</span>
                        <span className="file-size">{(item.file.size / 1024 / 1024).toFixed(2)} MB</span>
                        <span className="photo-type">
                          {item.type === 'profile' ? '👤 Profile' : item.type === 'gallery' ? '📸 Gallery' : '🔒 Private'}
                        </span>
                        {uploadProgress[index] && (
                          <span className={`upload-status ${uploadProgress[index].status}`}>
                            {uploadProgress[index].message}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        className="remove-photo-btn"
                        onClick={() => {
                          setSelectedFiles(prev => prev.filter((_, i) => i !== index));
                          setPreviews(prev => prev.filter((_, i) => i !== index));
                        }}
                        title="Remove photo"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-photo-preview">
                  <div className="no-photo-icon">📷</div>
                  <p>No photos selected yet</p>
                  <p className="no-photo-subtitle">
                    Click "Select Photo{selectionMode === 'multiple' ? 's' : ''}" to choose from your device
                  </p>
                </div>
              )}
            </div>
            </form>
          </div>
        </div>

        {/* Fixed Footer */}
        <div className="photo-upload-modal-footer">
          <button
            type="button"
            className="photo-modal-btn photo-modal-cancel"
            onClick={onClose}
            disabled={uploading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="photo-modal-btn photo-modal-upload"
            disabled={
              uploading ||
              selectedFiles.length === 0 ||
              (activeTab === 'gallery' && currentNormalPhotos >= 6) ||
              (activeTab === 'private' && currentPrivatePhotos >= 6)
            }
            onClick={() => {
              const form = document.querySelector('.photo-upload-modal-body form');
              if (form) {
                const submitEvent = new Event('submit', { cancelable: true, bubbles: true });
                form.dispatchEvent(submitEvent);
              }
            }}
          >
            {uploading ? (
              <>
                <span className="upload-spinner">⟳</span>
                Uploading {selectedFiles.length} photo{selectedFiles.length !== 1 ? 's' : ''}...
              </>
            ) : (
              (activeTab === 'gallery' && currentNormalPhotos >= 6) ||
              (activeTab === 'private' && currentPrivatePhotos >= 6) ?
                `Maximum ${activeTab === 'gallery' ? '6 public' : '6 private'} photos reached` :
                `Upload ${selectedFiles.length || ''} Photo${selectedFiles.length !== 1 ? 's' : ''}`
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default PhotoUploadModal;

